/**
 * The only place a path string is written, and the only place a matter key is spelled.
 *
 * `:matter` in a URL is a slug — `trade-marks`, `patents` — because a URL is read by people.
 * The wire carries the register's own stored spelling. The mapping between the two lives here
 * and nowhere else, so adding a matter is a row in this table rather than a search across the
 * codebase (§4.2, and `deferred.md` §8).
 */

export type MatterKey = string;
export type MatterSlug = string;

const SLUG_BY_KEY: Readonly<Record<string, string>> = {
  tm: 'trade-marks',
  pt: 'patents',
};

const KEY_BY_SLUG: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(SLUG_BY_KEY).map(([key, slug]) => [slug, key]),
);

/**
 * A matter key the mapping has never heard of falls back to itself, so a matter added to the
 * backend's vocabulary is reachable by URL before anyone edits this file.
 */
export function slugForMatter(key: MatterKey): MatterSlug {
  return SLUG_BY_KEY[key] ?? key;
}

export function matterForSlug(slug: MatterSlug): MatterKey {
  return KEY_BY_SLUG[slug] ?? slug;
}

export type DateParams = Partial<{
  filing: string;
  priority: string;
  publication: string;
  registration: string;
  grant: string;
  asAt: string;
  recordedAt: string;
}>;

function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : '';
}

const matterBase = (code: string, matter: MatterKey) =>
  `/jurisdictions/${encodeURIComponent(code)}/${slugForMatter(matter)}`;

export const routes = {
  home: () => '/',
  jurisdictions: (params: Record<string, string | number | undefined> = {}) =>
    `/jurisdictions${query(params)}`,
  jurisdiction: (code: string) => `/jurisdictions/${encodeURIComponent(code)}`,
  overview: (code: string) => `/jurisdictions/${encodeURIComponent(code)}/overview`,
  matter: (code: string, matter: MatterKey, dates: DateParams = {}) =>
    `${matterBase(code, matter)}${query(dates)}`,
  timeline: (code: string, matter: MatterKey, dates: DateParams = {}) =>
    `${matterBase(code, matter)}/timeline${query(dates)}`,
  rules: (code: string, matter: MatterKey, dates: DateParams = {}) =>
    `${matterBase(code, matter)}/rules${query(dates)}`,
  ruleEditor: (code: string, matter: MatterKey, lane: string, dates: DateParams = {}) =>
    `${matterBase(code, matter)}/rules/${encodeURIComponent(lane)}${query(dates)}`,
  newRule: (code: string, matter: MatterKey, dates: DateParams = {}) =>
    `${matterBase(code, matter)}/rules/new${query(dates)}`,
  ruleHistory: (code: string, matter: MatterKey, lane: string, recordedAt?: string) =>
    `${matterBase(code, matter)}/rules/${encodeURIComponent(lane)}/history${query({ recordedAt })}`,
  sources: (code: string, matter: MatterKey) => `${matterBase(code, matter)}/sources`,
  gaps: (code: string, matter: MatterKey) => `${matterBase(code, matter)}/gaps`,
  // Without a matter, the screen opens on the first one the caller is entitled to. The nav
  // cannot name a matter without putting a second copy of that vocabulary in the browser.
  coverage: (matter?: MatterKey) =>
    matter === undefined ? '/coverage' : `/coverage/${slugForMatter(matter)}`,
  admin: () => '/admin',
  adminPeople: () => '/admin/people',
  adminInterchange: () => '/admin/interchange',
  adminSources: () => '/admin/sources',
  adminRegisters: () => '/admin/registers',
  /**
   * The help portal. A static site served at this origin, outside the application — so this is
   * used as an `href` and never pushed onto the router, which has no such route.
   */
  help: () => '/help/',
  ruleCatalogue: (params: Record<string, string | undefined> = {}) => `/rules${query(params)}`,
  queue: (params: Record<string, string | number | undefined> = {}) => `/queue${query(params)}`,
  queueEntry: (id: string) => `/queue/${encodeURIComponent(id)}`,
  review: () => '/review',
  proposal: (id: string, change?: string) =>
    `/review/${encodeURIComponent(id)}${query({ change })}`,
  prompts: () => '/prompts',
  prompt: (id: string, mode?: 'edit' | 'preview') =>
    `/prompts/${encodeURIComponent(id)}${query({ mode })}`,
  promptVersions: (id: string) => `/prompts/${encodeURIComponent(id)}/versions`,
  mappings: () => '/mappings',
  contradictions: (params: Record<string, string | undefined> = {}) =>
    `/contradictions${query(params)}`,
  runs: (params: Record<string, string | number | undefined> = {}) => `/runs${query(params)}`,
  run: (id: string) => `/runs/${encodeURIComponent(id)}`,

  api: {
    meta: () => '/meta',
    authConfig: () => '/auth/config',
    login: () => '/auth/login',
    jurisdictions: () => '/jurisdictions',
    jurisdiction: (code: string) => `/jurisdictions/${encodeURIComponent(code)}`,
    lifecycleEvents: (code: string) => `/jurisdictions/${encodeURIComponent(code)}/lifecycle-events`,
    lifecycleEvent: (code: string, id: string) =>
      `/jurisdictions/${encodeURIComponent(code)}/lifecycle-events/${encodeURIComponent(id)}`,
    validateLifecycle: (code: string) =>
      `/jurisdictions/${encodeURIComponent(code)}/lifecycle-events/validate`,
    successions: (code: string) => `/jurisdictions/${encodeURIComponent(code)}/successions`,
    succession: (code: string, id: string) =>
      `/jurisdictions/${encodeURIComponent(code)}/successions/${encodeURIComponent(id)}`,
    matter: (code: string, matter: MatterKey) =>
      `/jurisdictions/${encodeURIComponent(code)}/matters/${encodeURIComponent(matter)}`,
    calculations: (code: string, matter: MatterKey) =>
      `/jurisdictions/${encodeURIComponent(code)}/matters/${encodeURIComponent(matter)}/calculations`,
    rule: (code: string, matter: MatterKey, lane: string) =>
      `/jurisdictions/${encodeURIComponent(code)}/matters/${encodeURIComponent(matter)}/rules/${encodeURIComponent(lane)}`,
    validateRule: (code: string, matter: MatterKey, lane: string) =>
      `/jurisdictions/${encodeURIComponent(code)}/matters/${encodeURIComponent(matter)}/rules/${encodeURIComponent(lane)}/validate`,
    ruleHistory: (code: string, matter: MatterKey, lane: string) =>
      `/jurisdictions/${encodeURIComponent(code)}/matters/${encodeURIComponent(matter)}/rules/${encodeURIComponent(lane)}/history`,
    clearContradiction: (code: string, matter: MatterKey, lane: string, versionId: string) =>
      `/jurisdictions/${encodeURIComponent(code)}/matters/${encodeURIComponent(matter)}/rules/${encodeURIComponent(lane)}/versions/${encodeURIComponent(versionId)}/clear-contradiction`,
    coverage: (matter: MatterKey) => `/coverage?matter=${encodeURIComponent(matter)}`,
    runImpact: (runId: string) => `/runs/${encodeURIComponent(runId)}/impact`,
    gaps: (code: string, matter: MatterKey) =>
      `/jurisdictions/${encodeURIComponent(code)}/matters/${encodeURIComponent(matter)}/gaps`,
    pendingChanges: (code: string, matter: MatterKey) =>
      `/jurisdictions/${encodeURIComponent(code)}/matters/${encodeURIComponent(matter)}/rules/pending`,
    approvePendingChange: (code: string, matter: MatterKey, id: string) =>
      `/jurisdictions/${encodeURIComponent(code)}/matters/${encodeURIComponent(matter)}/rules/pending/${encodeURIComponent(id)}/approve`,
    rejectPendingChange: (code: string, matter: MatterKey, id: string) =>
      `/jurisdictions/${encodeURIComponent(code)}/matters/${encodeURIComponent(matter)}/rules/pending/${encodeURIComponent(id)}/reject`,
    sources: (code: string, matter: MatterKey) =>
      `/jurisdictions/${encodeURIComponent(code)}/matters/${encodeURIComponent(matter)}/sources`,
    validateSource: (code: string, matter: MatterKey) =>
      `/jurisdictions/${encodeURIComponent(code)}/matters/${encodeURIComponent(matter)}/sources/validate`,
    users: () => '/users',
    rules: () => '/rules',
    imports: () => '/imports',
    oneImport: (id: string) => `/imports/${encodeURIComponent(id)}`,
    importRows: (id: string) => `/imports/${encodeURIComponent(id)}/rows`,
    commitImport: (id: string) => `/imports/${encodeURIComponent(id)}/commit`,
    exports: () => '/exports',
    invitations: () => '/users/invitations',
    user: (id: string) => `/users/${encodeURIComponent(id)}`,
    contradictions: () => '/contradictions',
    mappings: () => '/mappings',
    mapping: (code: string, matter: string) =>
      `/mappings/${encodeURIComponent(code)}/${encodeURIComponent(matter)}`,
    queue: () => '/queue',
    queueEntry: (id: string) => `/queue/${encodeURIComponent(id)}`,
    queueCancel: (id: string) => `/queue/${encodeURIComponent(id)}/cancel`,
    queueRetry: (id: string) => `/queue/${encodeURIComponent(id)}/retry`,
    runs: () => '/runs',
    run: (id: string) => `/runs/${encodeURIComponent(id)}`,
    runEvents: (id: string) => `/runs/${encodeURIComponent(id)}/events`,
    prompts: () => '/prompts',
    prompt: (id: string) => `/prompts/${encodeURIComponent(id)}`,
    promptVersions: (id: string) => `/prompts/${encodeURIComponent(id)}/versions`,
    validatePrompt: (id: string) => `/prompts/${encodeURIComponent(id)}/validate`,
    previewPrompt: (id: string) => `/prompts/${encodeURIComponent(id)}/preview`,
    proposals: () => '/proposals',
    proposal: (id: string) => `/proposals/${encodeURIComponent(id)}`,
    confirmProposal: (id: string) => `/proposals/${encodeURIComponent(id)}/confirm`,
  },
} as const;
